import { useState } from 'react';
import { WorkCalendarSummary } from '../../../services/apiClient';
import { EmptyState } from '../../ui/EmptyState';

const REGIONS: { value: string; label: string }[] = [
  { value: '', label: '— (sin autonómicos)' },
  { value: 'CAT', label: 'CAT · Catalunya' },
  { value: 'MAD', label: 'MAD · Madrid' },
];

interface CalendarListProps {
  calendars: WorkCalendarSummary[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onCreate: (data: { name: string; year: number; region: string | null }) => Promise<void>;
  onActivate: (id: number) => Promise<void>;
  onSeed: (id: number) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onClearMessages: () => void;
}

/**
 * Panel izquierdo: alta de calendario y lista con las acciones por fila
 * (activar / sembrar / borrar con confirmación). El botón y el formulario de
 * alta llevan `lg:col-span-2` porque comparten el grid de dos columnas del
 * orquestador (`grid-cols-[380px_1fr]`) — así ocupan el ancho completo
 * exactamente como antes de partir el fichero, y la lista propiamente dicha
 * queda en la columna de 380px.
 *
 * El estado de estos sub-formularios (alta, confirmación de borrado por
 * fila) es local: el orquestador no lo necesita para nada más.
 */
export function CalendarList({
  calendars,
  selectedId,
  onSelect,
  onCreate,
  onActivate,
  onSeed,
  onDelete,
  onClearMessages,
}: CalendarListProps) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newYear, setNewYear] = useState(String(new Date().getFullYear()));
  const [newRegion, setNewRegion] = useState('');
  const [creating, setCreating] = useState(false);

  const [seedingId, setSeedingId] = useState<number | null>(null);
  const [activatingId, setActivatingId] = useState<number | null>(null);
  const [confirmDeleteCalId, setConfirmDeleteCalId] = useState<number | null>(null);
  const [deletingCalId, setDeletingCalId] = useState<number | null>(null);

  const submitCreate = async () => {
    const name = newName.trim();
    const year = parseInt(newYear || '0', 10);
    if (!name || !year) return;
    setCreating(true);
    try {
      await onCreate({ name, year, region: newRegion || null });
      setShowCreate(false);
      setNewName('');
      setNewRegion('');
    } catch {
      // El orquestador ya ha fijado el error; el formulario sigue abierto
      // con los valores tal cual para poder reintentar.
    } finally {
      setCreating(false);
    }
  };

  const handleActivateClick = async (calId: number) => {
    setActivatingId(calId);
    try {
      await onActivate(calId);
    } finally {
      setActivatingId(null);
    }
  };

  const handleSeedClick = async (calId: number) => {
    setSeedingId(calId);
    try {
      await onSeed(calId);
    } finally {
      setSeedingId(null);
    }
  };

  const handleDeleteClick = (calId: number) => {
    if (confirmDeleteCalId !== calId) {
      setConfirmDeleteCalId(calId);
      return;
    }
    void doDelete(calId);
  };

  const doDelete = async (calId: number) => {
    setDeletingCalId(calId);
    try {
      await onDelete(calId);
    } finally {
      setDeletingCalId(null);
      setConfirmDeleteCalId(null);
    }
  };

  return (
    <>
      <div className="flex justify-end lg:col-span-2">
        <button
          type="button"
          onClick={() => {
            setShowCreate((v) => !v);
            onClearMessages();
          }}
          className="h-[42px] px-5 rounded-xl bg-brand text-on-brand font-bold text-[14px]"
        >
          {showCreate ? 'Cerrar' : '+ Nuevo calendario'}
        </button>
      </div>

      {showCreate && (
        <div className="card-surface p-5 space-y-4 animate-rise lg:col-span-2">
          <p className="font-bold text-[16px]">Nuevo calendario</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
            <div>
              <label className="label">Nombre</label>
              <input
                className="field"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="p. ej. Calendario 2026"
                autoFocus
              />
            </div>
            <div>
              <label className="label">Año</label>
              <input
                className="field font-mono"
                inputMode="numeric"
                value={newYear}
                onChange={(e) => setNewYear(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
              />
            </div>
            <div>
              <label className="label">Región</label>
              <select className="field" value={newRegion} onChange={(e) => setNewRegion(e.target.value)}>
                {REGIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-[12.5px] text-ink-3">
            El sembrado automático incluye autonómicos de Catalunya y Madrid; el resto se añaden a mano.
          </p>
          <button
            type="button"
            onClick={submitCreate}
            disabled={!newName.trim() || !parseInt(newYear || '0', 10) || creating}
            className="btn-primary h-12 max-w-[280px] text-[15px]"
          >
            {creating ? 'Creando y sembrando…' : 'Crear calendario'}
          </button>
        </div>
      )}

      <div className="rounded-2xl bg-surface border border-line overflow-hidden">
        <div className="px-4.5 px-5 py-3 bg-surface-2 font-mono text-[11px] uppercase text-ink-3">
          Calendarios
        </div>
        {calendars.map((c) => {
          const isSelected = c.id === selectedId;
          return (
            <div
              key={c.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(c.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(c.id);
                }
              }}
              className={`w-full text-left border-t border-line px-4.5 px-5 py-3.5 transition-colors cursor-pointer ${
                isSelected ? 'bg-brand-soft' : 'hover:bg-surface-2'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className={`font-semibold text-[14.5px] ${isSelected ? 'text-brand' : ''}`}>{c.name}</p>
                {c.is_active && (
                  <span className="font-mono text-[10.5px] font-bold bg-brand text-on-brand rounded-full px-2 py-0.5 shrink-0">
                    ACTIVO
                  </span>
                )}
              </div>
              <p className="text-[12.5px] text-ink-2 mt-0.5">
                {c.year} · {c.region ?? 'sin región'} · {c.holiday_count} festivo{c.holiday_count === 1 ? '' : 's'}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-2.5">
                {!c.is_active && (
                  <button
                    type="button"
                    onClick={() => handleActivateClick(c.id)}
                    disabled={activatingId === c.id}
                    className="h-8 px-2.5 rounded-lg border border-line-strong font-semibold text-[12px] disabled:opacity-40"
                  >
                    {activatingId === c.id ? '…' : 'Activar'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleSeedClick(c.id)}
                  disabled={seedingId === c.id}
                  className="h-8 px-2.5 rounded-lg border border-line-strong font-semibold text-[12px] disabled:opacity-40"
                >
                  {seedingId === c.id ? 'Sembrando…' : 'Sembrar'}
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteClick(c.id)}
                  disabled={deletingCalId === c.id}
                  className="h-8 px-2.5 rounded-lg border border-danger text-danger font-semibold text-[12px] disabled:opacity-40 ml-auto"
                >
                  {deletingCalId === c.id
                    ? 'Borrando…'
                    : confirmDeleteCalId === c.id
                      ? '¿Seguro?'
                      : 'Borrar'}
                </button>
              </div>
            </div>
          );
        })}
        {calendars.length === 0 && (
          <div className="p-2">
            <EmptyState
              icon="calendar"
              title="Aún no hay calendarios"
              body="Crea el calendario laboral del año en curso y siembra sus festivos nacionales y autonómicos."
              ctaLabel="Nuevo calendario"
              onCta={() => setShowCreate(true)}
            />
          </div>
        )}
      </div>
    </>
  );
}
